import React, { useState } from 'react';
import { Sparkles, HelpCircle, Copy, Check, Bot, MessageSquare, Plus, Trash2, Zap } from 'lucide-react';
import { KeywordResearchProject } from '../../types';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface QuestionAeoWorkstationProps {
  project: KeywordResearchProject;
  masterPrimary: string;
  masterSecondary: string;
  onUpdateProject: (updated: KeywordResearchProject) => void;
}

interface QuestionItem {
  id: string;
  question: string;
  directAnswer: string;
  source: 'Google PAA' | 'AnswerThePublic' | 'Reddit/Quora' | 'Ahrefs/Semrush';
}

export const QuestionAeoWorkstation: React.FC<QuestionAeoWorkstationProps> = ({
  project,
  masterPrimary,
  masterSecondary,
  onUpdateProject,
}) => {
  const [selectedPageKey, setSelectedPageKey] = useState<string>('homepage');
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const [showAiDrawer, setShowAiDrawer] = useState<boolean>(false);

  const businessName = project.businessProfile.businessName || 'Business';
  const location = (project.businessProfile.serviceAreas && project.businessProfile.serviceAreas[0]) || project.businessProfile.primaryLocation || project.businessProfile.country || '';
  const servicePages = project.servicePageMappings || [];

  // Default initial question pools per page
  const [pageQuestionsMap, setPageQuestionsMap] = useState<Record<string, QuestionItem[]>>({
    homepage: [
      {
        id: 'q-1',
        question: `How to find the best ${masterPrimary || project.businessProfile.confirmedServices[0] || 'service provider'}${location ? ` in ${location}` : ''}?`,
        directAnswer: `To find the best ${masterPrimary || project.businessProfile.confirmedServices[0] || 'service provider'}${location ? ` in ${location}` : ''}, look for established providers with verified customer reviews, clear service guarantees, and fast local response times.`,
        source: 'Google PAA'
      },
      {
        id: 'q-2',
        question: `Where can I order commercial ${masterSecondary || 'auto parts'} in ${location}?`,
        directAnswer: `You can order commercial ${masterSecondary || 'auto parts'} directly through ${businessName}, offering comprehensive inventory, certified fitment guarantees, and direct delivery across ${location}.`,
        source: 'AnswerThePublic'
      }
    ]
  });

  const [newQText, setNewQText] = useState('');
  const [newAText, setNewAText] = useState('');
  const [newQSource, setNewQSource] = useState<'Google PAA' | 'AnswerThePublic' | 'Reddit/Quora' | 'Ahrefs/Semrush'>('Google PAA');

  // Get active page details
  let activePageName = 'Homepage (/)';
  let activeTargetUrl = '/';
  let activePrimary = masterPrimary;

  if (selectedPageKey !== 'homepage') {
    const sp = servicePages.find(s => s.id === selectedPageKey);
    if (sp) {
      activePageName = sp.pageName || 'Service Page';
      activeTargetUrl = sp.targetUrl || '/services/page';
      activePrimary = sp.primaryKeyword || 'Primary Target';
    }
  }

  const currentQuestions = pageQuestionsMap[selectedPageKey] || [];

  const handleAddQuestion = () => {
    if (!newQText.trim()) return;
    const newItem: QuestionItem = {
      id: `q-${Date.now()}`,
      question: newQText.trim(),
      directAnswer: newAText.trim(),
      source: newQSource
    };
    setPageQuestionsMap({
      ...pageQuestionsMap,
      [selectedPageKey]: [...currentQuestions, newItem]
    });
    setNewQText('');
    setNewAText('');
  };

  const handleDeleteQuestion = (id: string) => {
    setPageQuestionsMap({
      ...pageQuestionsMap,
      [selectedPageKey]: currentQuestions.filter(q => q.id !== id)
    });
  };

  const [aeoJsonInput, setAeoJsonInput] = useState<string>('');
  const [aeoSuccessMsg, setAeoSuccessMsg] = useState<string>('');

  const handleApplyAeoJson = (jsonString: string) => {
    try {
      if (!jsonString || !jsonString.trim()) {
        alert("Please paste the JSON array output from ChatGPT / Claude / Gemini into the text box.");
        return;
      }
      const cleanedStr = jsonString.replace(/```json/gi, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(cleanedStr);

      const items = Array.isArray(parsed) ? parsed : (parsed.questions || [parsed]);
      if (!items || items.length === 0) {
        throw new Error("No question objects found in JSON.");
      }

      const newQuestions: QuestionItem[] = items.map((q: any, i: number) => ({
        id: `q-${Date.now()}-${i}`,
        question: (q.question || q.h2 || q.subheading || '').replace(/^<h2>|<\/h2>$/gi, '').trim(),
        directAnswer: (q.directAnswer || q.answer || q.conciseAnswer || '').trim(),
        source: (q.source && ['Google PAA', 'AnswerThePublic', 'Reddit/Quora', 'Ahrefs/Semrush'].includes(q.source))
          ? q.source
          : 'Google PAA'
      })).filter((q: QuestionItem) => q.question.length > 0);

      if (newQuestions.length === 0) {
        throw new Error("Could not extract valid question objects from JSON.");
      }

      // Update local state map for selected page
      const updatedMap = {
        ...pageQuestionsMap,
        [selectedPageKey]: [...(pageQuestionsMap[selectedPageKey] || []), ...newQuestions]
      };
      setPageQuestionsMap(updatedMap);

      // Also update project.aeoQuestions for global project persistence
      const updatedProjectAeo = [...(project.aeoQuestions || [])];
      newQuestions.forEach(nq => {
        updatedProjectAeo.push({
          id: nq.id,
          question: nq.question,
          source: nq.source.includes('PAA') ? 'PAA' : nq.source.includes('Reddit') ? 'Forum' : 'AnswerThePublic',
          intent: 'Informational / Commercial',
          relevantPage: activePageName,
          answerOpportunity: 'H2 Heading',
          priority: 'P1',
          conciseAnswer: nq.directAnswer,
          verificationStatus: 'user_verified'
        });
      });

      onUpdateProject({
        ...project,
        aeoQuestions: updatedProjectAeo
      });

      setAeoJsonInput('');
      setAeoSuccessMsg(`✓ Successfully auto-filled ${newQuestions.length} AEO Questions & Direct Answers!`);
      setTimeout(() => setAeoSuccessMsg(''), 4000);
    } catch (err: any) {
      alert(`Failed to parse AEO JSON: ${err.message || 'Invalid format'}. Please ensure you copy and paste the raw JSON array.`);
    }
  };

  // Build AEO Prompt
  const buildAeoPrompt = () => {
    return `You are an Answer Engine Optimization (AEO) Content Specialist optimizing content for Google Voice Search, Google AI Overviews, ChatGPT, and Perplexity.

PAGE & BUSINESS CONTEXT:
- Business Name: ${businessName}
- Target Location / Region: ${location}
- Page Name: ${activePageName}
- Target URL Path: ${activeTargetUrl}
- Target Primary Keyword: "${activePrimary}"

RESEARCH SOURCES TO INSPECT & REFERENCE (LECTURE METHODOLOGY):
- Google People Also Ask (PAA)
- Reddit & Quora Community Discussions
- AnswerThePublic / AnswerOnSocrates Queries
- Industry Niche Forums & Voice Search Questions

STRICT AEO QUESTION & DIRECT ANSWER RULES:
1. QUANTITY & COVERAGE: Generate as many high-impact FAQs / AEO Question Headings as possible (minimum 8-12 comprehensive questions) to maximize search coverage across Google Voice Search, AI Overviews, and Featured Snippets.
2. SOURCING: Questions MUST be derived from real user queries found on Google PAA, Reddit, Quora, AnswerThePublic, or AnswerOnSocrates.
3. FORMAT: Format each question as an exact <h2> Subheading (e.g. <h2>How to find the best ${activePrimary} in ${location}?</h2>).
4. DIRECT ANSWER: Immediately beneath each <h2> heading, write a concise 40-50 word DIRECT ANSWER.
5. NO FILLER: The Direct Answer MUST be conversational, direct, and human — NO fluff, NO rambling introductory definition filler ("A general contractor is...").
6. AI OVERVIEWS READY: Provide immediate value to answer the user's conversational intent so Google AI can extract the snippet directly into AI Overviews and Voice Search.

STRICT OUTPUT FORMAT REQUIRED:
Return ONLY a valid JSON array matching EXACTLY the format below (no markdown wrappers, no conversational text, no preambles):

[
  {
    "question": "How to find the best ${activePrimary} in ${location}?",
    "directAnswer": "To find the best ${activePrimary} in ${location}, look for established distributors with verified customer reviews, genuine OEM warranty coverage, and fast regional delivery options for commercial fleets.",
    "source": "Google PAA"
  },
  {
    "question": "Where can I order heavy-duty replacement parts in ${location}?",
    "directAnswer": "You can order heavy-duty commercial parts directly through ${businessName} in ${location}. We stock certified OEM components, braking systems, and engine spares with full warranty protection.",
    "source": "Reddit/Quora"
  }
]`;
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(buildAeoPrompt());
    setCopiedPromptId(selectedPageKey);
    setTimeout(() => setCopiedPromptId(null), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* HEADER BANNER */}
      <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <HelpCircle size={18} color="var(--accent-blue)" />
          <span>STEP 14: ANSWER ENGINE OPTIMIZATION (AEO) & CONVERSATIONAL QUESTIONS</span>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Instructor Methodology: Structure conversational questions as <strong>&lt;h2&gt; Subheadings</strong> followed immediately by a concise <strong>40–50 word Direct Answer</strong> (no fluff) to rank in Google AI Overviews, Voice Search & Chatbots.
        </div>

        {/* CONTROLS BAR */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px', background: '#020617', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>Select Page:</label>
            <select
              className="input-field"
              style={{ padding: '6px 10px', fontSize: '12px', background: '#0f172a', color: '#ffffff', fontWeight: 700 }}
              value={selectedPageKey}
              onChange={(e) => setSelectedPageKey(e.target.value)}
            >
              <option value="homepage">Homepage (/)</option>
              {servicePages.map((sp, idx) => (
                <option key={sp.id} value={sp.id}>
                  Service #{idx + 1}: {sp.pageName || `Service Page ${idx + 1}`} ({sp.targetUrl})
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="secondary"
            size="sm"
            style={{ color: '#c084fc', borderColor: '#a855f7', background: 'rgba(168, 85, 247, 0.12)', fontSize: '11px', fontWeight: 700 }}
            onClick={() => setShowAiDrawer(!showAiDrawer)}
          >
            <Sparkles size={13} style={{ marginRight: '4px' }} />
            AI AEO Prompt Generator & Auto-Fill
          </Button>
        </div>
      </div>

      {/* AI PROMPT & AUTO-FILL DRAWER */}
      {showAiDrawer && (
        <div style={{ background: '#090d16', border: '1px solid rgba(168, 85, 247, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#c084fc', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Bot size={16} />
            <span>AEO QUESTION & DIRECT ANSWER PROMPT GENERATOR & 1-CLICK AUTO-FILL</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
            {/* LEFT: Pre-filled Prompt */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#e2e8f0' }}>Step 1: Copy Pre-Filled AEO Prompt</span>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ fontSize: '11px', background: copiedPromptId === selectedPageKey ? '#166534' : 'rgba(168, 85, 247, 0.2)', borderColor: '#a855f7', color: '#fff' }}
                  onClick={handleCopyPrompt}
                >
                  {copiedPromptId === selectedPageKey ? <Check size={12} style={{ marginRight: '4px' }} /> : <Copy size={12} style={{ marginRight: '4px' }} />}
                  {copiedPromptId === selectedPageKey ? 'Copied Prompt!' : 'Copy AEO Prompt'}
                </Button>
              </div>
              <textarea
                readOnly
                rows={8}
                className="input-field"
                style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', background: '#020617', width: '100%', lineHeight: '1.4' }}
                value={buildAeoPrompt()}
              />
            </div>

            {/* RIGHT: Paste JSON & Auto-Fill */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>Step 2: Paste ChatGPT / Gemini Output JSON & Auto-Fill</span>
                {aeoSuccessMsg && (
                  <span style={{ fontSize: '10px', color: '#34d399', fontWeight: 700 }}>{aeoSuccessMsg}</span>
                )}
              </div>
              <textarea
                rows={6}
                className="input-field"
                placeholder={`Paste raw LLM JSON array response here...\n\nExample:\n[\n  {\n    "question": "How to find the best ${activePrimary} in ${location}?",\n    "directAnswer": "To find the best ${activePrimary} in ${location}, choose established suppliers offering genuine OEM components with verified fitment guarantees...",\n    "source": "Google PAA"\n  }\n]`}
                style={{ fontSize: '11px', fontFamily: 'monospace', color: '#ffffff', width: '100%', marginBottom: '8px' }}
                value={aeoJsonInput}
                onChange={(e) => setAeoJsonInput(e.target.value)}
              />
              <Button
                variant="primary"
                size="sm"
                style={{ width: '100%', fontSize: '12px', background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', fontWeight: 800 }}
                onClick={() => handleApplyAeoJson(aeoJsonInput)}
              >
                <Zap size={14} style={{ marginRight: '6px' }} />
                Auto-Fill {activePageName} Questions & Answers
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QUESTION MANAGER WORKSTATION */}
      <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px' }}>
        
        {/* ADD NEW QUESTION BLOCK */}
        <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={14} /> Add Conversational AEO Question & Direct Answer to {activePageName}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '10px', marginBottom: '8px' }}>
            <input
              type="text"
              className="input-field"
              style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700 }}
              placeholder='Conversational Question (e.g. "How to find the best general contractor in Calgary?")'
              value={newQText}
              onChange={(e) => setNewQText(e.target.value)}
            />
            <select
              className="input-field"
              style={{ padding: '8px 10px', fontSize: '11px', background: '#0f172a', color: '#fff', fontWeight: 700 }}
              value={newQSource}
              onChange={(e) => setNewQSource(e.target.value as any)}
            >
              <option value="Google PAA">Google PAA</option>
              <option value="AnswerThePublic">AnswerThePublic</option>
              <option value="Reddit/Quora">Reddit / Quora</option>
              <option value="Ahrefs/Semrush">Ahrefs / Semrush</option>
            </select>
          </div>

          <textarea
            className="input-field"
            rows={3}
            style={{ width: '100%', padding: '8px 10px', fontSize: '12px', marginBottom: '10px' }}
            placeholder="Direct Answer (40–50 words, conversational tone, concise, no fluff)..."
            value={newAText}
            onChange={(e) => setNewAText(e.target.value)}
          />

          <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={handleAddQuestion}>
            Add Question & Direct Answer
          </Button>
        </div>

        {/* ACTIVE QUESTIONS LIST */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MessageSquare size={16} color="var(--accent-green)" />
            <span>FORMATTED H2 QUESTION HEADINGS & DIRECT ANSWERS ({currentQuestions.length} ITEMS)</span>
          </div>

          {currentQuestions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '12px' }}>
              No AEO questions added for this page yet. Use the prompt or form above to populate!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentQuestions.map((item, idx) => {
                const wordCount = item.directAnswer.trim() ? item.directAnswer.trim().split(/\s+/).length : 0;
                return (
                  <div key={item.id} style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Badge variant="lecture">&lt;H2&gt; Heading</Badge>
                        <Badge variant="tool">{item.source}</Badge>
                      </div>
                      <button
                        onClick={() => handleDeleteQuestion(item.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', marginBottom: '6px' }}>
                      Q{idx + 1}: {item.question}
                    </div>

                    <div style={{ background: '#020617', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '10px', fontSize: '12px', color: '#e2e8f0', lineHeight: '1.5' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#34d399', fontWeight: 800 }}>DIRECT ANSWER COPY:</span>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{wordCount} Words {wordCount >= 40 && wordCount <= 50 ? '✓ Ideal (40-50w)' : ''}</span>
                      </div>
                      {item.directAnswer}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
